import React, { useState, useEffect } from 'react';

const SudokuGame = () => {
  const [board, setBoard] = useState(Array(9).fill(Array(9).fill(0)));
  const [initialBoard, setInitialBoard] = useState(Array(9).fill(Array(9).fill(0)));
  const [selectedCell, setSelectedCell] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [errors, setErrors] = useState(new Set());
  const [difficulty, setDifficulty] = useState('medium'); // easy, medium, hard

  // Utility function to create a deep copy of a 2D array
  const deepCopy = (arr) => arr.map(row => [...row]);

  // Check if a number can be placed in the given position
  const isValid = (board, row, col, num) => {
    // Check row
    for (let x = 0; x < 9; x++) {
      if (board[row][x] === num) return false;
    }

    // Check column
    for (let x = 0; x < 9; x++) {
      if (board[x][col] === num) return false;
    }

    // Check 3x3 box
    const startRow = Math.floor(row / 3) * 3;
    const startCol = Math.floor(col / 3) * 3;
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        if (board[startRow + i][startCol + j] === num) return false;
      }
    }

    return true;
  };

  // Fill diagonal 3x3 boxes (which are independent of each other)
  const fillDiagonal = (board) => {
    for (let box = 0; box < 9; box += 3) {
      let numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
          const randomIndex = Math.floor(Math.random() * numbers.length);
          board[box + i][box + j] = numbers[randomIndex];
          numbers.splice(randomIndex, 1);
        }
      }
    }
  };

  // Solve the Sudoku using backtracking
  const solveSudoku = (board) => {
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (board[row][col] === 0) {
          for (let num = 1; num <= 9; num++) {
            if (isValid(board, row, col, num)) {
              board[row][col] = num;
              if (solveSudoku(board)) return true;
              board[row][col] = 0;
            }
          }
          return false;
        }
      }
    }
    return true;
  };

  // Generate a complete solved Sudoku puzzle
  const generateSolvedPuzzle = () => {
    const board = Array(9).fill().map(() => Array(9).fill(0));
    fillDiagonal(board);
    solveSudoku(board);
    return board;
  };

  // Remove numbers to create the puzzle based on difficulty
  const createPuzzle = (solved, difficulty) => {
    const puzzle = deepCopy(solved);
    let cellsToRemove;
    
    switch(difficulty) {
      case 'easy':
        cellsToRemove = 40; // Leave 41 numbers
        break;
      case 'hard':
        cellsToRemove = 60; // Leave 21 numbers
        break;
      default: // medium
        cellsToRemove = 50; // Leave 31 numbers
    }

    let removed = 0;
    while (removed < cellsToRemove) {
      const row = Math.floor(Math.random() * 9);
      const col = Math.floor(Math.random() * 9);
      if (puzzle[row][col] !== 0) {
        puzzle[row][col] = 0;
        removed++;
      }
    }
    return puzzle;
  };

  const [solvedBoard, setSolvedBoard] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [playerBoard, setPlayerBoard] = useState(null);

  // Toggle between solution and player's progress
  const toggleSolution = () => {
    if (isRevealed) {
      // Hide solution and restore player's progress
      setBoard(deepCopy(playerBoard));
      setIsRevealed(false);
    } else {
      // Store current progress and show solution
      setPlayerBoard(deepCopy(board));
      setBoard(deepCopy(solvedBoard));
      setIsRevealed(true);
    }
  };

  // Generate a new Sudoku puzzle
  const generateNewPuzzle = () => {
    const solvedPuzzle = generateSolvedPuzzle();
    const newPuzzle = createPuzzle(solvedPuzzle, difficulty);
    setBoard(deepCopy(newPuzzle));
    setInitialBoard(deepCopy(newPuzzle));
    setSolvedBoard(deepCopy(solvedPuzzle));
    setErrors(new Set());
    setIsComplete(false);
    setSelectedCell(null);
    setIsRevealed(false);
    setPlayerBoard(null);
  };

  // Check if the move is valid
  const isValidMove = (row, col, num) => {
    // Check row
    for (let x = 0; x < 9; x++) {
      if (board[row][x] === num && x !== col) return false;
    }

    // Check column
    for (let x = 0; x < 9; x++) {
      if (board[x][col] === num && x !== row) return false;
    }

    // Check 3x3 box
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        if (board[boxRow + i][boxCol + j] === num && 
            (boxRow + i !== row || boxCol + j !== col)) {
          return false;
        }
      }
    }

    return true;
  };

  // Handle cell click
  const handleCellClick = (row, col) => {
    if (initialBoard[row][col] === 0) {
      setSelectedCell([row, col]);
    }
  };

  // Handle number input
  const handleNumberInput = (num, row, col) => {
    if (initialBoard[row][col] !== 0) return;

    const newBoard = deepCopy(board);
    newBoard[row][col] = num;
    setBoard(newBoard);

    // Check if the move is valid
    if (num !== 0 && !isValidMove(row, col, num)) {
      setErrors(new Set([...errors, `${row}-${col}`]));
    } else {
      const newErrors = new Set(errors);
      newErrors.delete(`${row}-${col}`);
      setErrors(newErrors);
    }

    // Check if puzzle is complete
    checkCompletion(newBoard);
  };

  // Handle difficulty change
  const handleDifficultyChange = (newDifficulty) => {
    setDifficulty(newDifficulty);
    setBoard(Array(9).fill(Array(9).fill(0)));
    generateNewPuzzle();
  };

  // Check if the puzzle is complete
  const checkCompletion = (currentBoard) => {
    const isComplete = currentBoard.every((row, rowIndex) =>
      row.every((cell, colIndex) =>
        cell !== 0 && isValidMove(rowIndex, colIndex, cell)
      )
    );
    setIsComplete(isComplete);
  };

  // Initialize the game
  useEffect(() => {
    generateNewPuzzle();
  }, []);

  return (
    <div className="w-full max-w-2xl mx-auto mt-8 bg-white rounded-lg shadow-md p-6">
      <div className="text-2xl font-bold mb-4 text-center">
        {isComplete ? "Congratulations! Puzzle Completed!" : "Sudoku"}
      </div>

      <div className="flex justify-center gap-4 mb-4">
        <button
          className={`px-4 py-2 rounded ${difficulty === 'easy' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
          onClick={() => handleDifficultyChange('easy')}
        >
          Easy
        </button>
        <button
          className={`px-4 py-2 rounded ${difficulty === 'medium' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
          onClick={() => handleDifficultyChange('medium')}
        >
          Medium
        </button>
        <button
          className={`px-4 py-2 rounded ${difficulty === 'hard' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
          onClick={() => handleDifficultyChange('hard')}
        >
          Hard
        </button>
      </div>
      
      <div className="grid grid-cols-9 gap-0.5 bg-gray-300 p-0.5 mb-4">
        {board.map((row, rowIndex) => (
          row.map((cell, colIndex) => (
            <input
              key={`${rowIndex}-${colIndex}`}
              type="text"
              maxLength="1"
              value={cell !== 0 ? cell : ''}
              readOnly={initialBoard[rowIndex][colIndex] !== 0}
              className={`
                w-12 h-12 text-center text-lg font-semibold outline-none
                ${(rowIndex + 1) % 3 === 0 && 'border-b-2 border-gray-400'}
                ${(colIndex + 1) % 3 === 0 && 'border-r-2 border-gray-400'}
                ${selectedCell && selectedCell[0] === rowIndex && selectedCell[1] === colIndex
                  ? 'bg-blue-200'
                  : initialBoard[rowIndex][colIndex] !== 0
                  ? 'bg-gray-100'
                  : 'bg-white hover:bg-blue-50'}
                ${errors.has(`${rowIndex}-${colIndex}`) ? 'text-red-500' : 'text-gray-800'}
                ${initialBoard[rowIndex][colIndex] !== 0 ? 'cursor-not-allowed' : 'cursor-text'}
              `}
              onChange={(e) => {
                const num = parseInt(e.target.value.replace(/[^1-9]/g, '')) || 0;
                handleNumberInput(num, rowIndex, colIndex);
              }}
              onClick={() => handleCellClick(rowIndex, colIndex)}
            />
          ))
        ))}
      </div>

      <div className="flex gap-4 mt-4">
        <button
          className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded"
          onClick={generateNewPuzzle}
        >
          New Game
        </button>
        <button
          className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded"
          onClick={toggleSolution}
        >
          {isRevealed ? 'Hide Solution' : 'Show Solution'}
        </button>
      </div>
    </div>
  );
};

export default SudokuGame;